import {
  Button,
  Heading,
  ListItem,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { getAppLinks } from '../../util/appLinks';
import { getBusinessDefaultURL } from '../../util/getBusinessDefaultURL';
import { EstimationInput, EstimationPresets, EstimationSubmit } from '../App';
import { EstimationTool } from '../components/EstimationTool';
import { EmissionCalculationInTonnes } from '../configs/industryCalculationTemplate';
import { getMerchantConfig, MerchantConfig } from '../configs/merchantConfigs';
const appLinks = getAppLinks();

type Props = {
  merchantConfig: MerchantConfig;
  onSubmitEstimate: (props: EstimationSubmit) => void;
  reset: () => void;
  countryCode: string;
};

export const EstimateView = ({
  onSubmitEstimate,
  merchantConfig,
  reset,
  countryCode
}: Props): JSX.Element => {
  const { estimationPresets, title } = merchantConfig;
  const [tabIndex, setTabIndex] = useState<number>(0);

  const [
    calculateEmissionsHrefBase,
    setCalculateEmissionsHrefBase,
  ] = useState<string>('');
  const onBusinessTabSelect = () => {
    setTabIndex(1);
    reset();
  };

  const onIndustryAvgClick = () => {
    setTabIndex(0);
  };

  const [calculateEmissionsHref, setCalculateEmissionsHref] = useState<string>(
    appLinks.carbonClickBusinessEmissionsUrl,
  );

  useEffect(() => {
    const paramsObj = new URLSearchParams(document.location.search);

    const merchantReferrerCode = paramsObj.get('merchantReferrerCode');
    let paramsString = '';
    if (merchantReferrerCode) {
      const merchantConfig = getMerchantConfig(merchantReferrerCode);
      if (merchantConfig.estimationPresets.industryId) {
        paramsString += `&industry=${encodeURIComponent(
          merchantConfig.estimationPresets.industryId,
        )}`;
      }
    }
    const url = `${calculateEmissionsHrefBase}${
      paramsString ? `${paramsString}` : ''
    }`;

    setCalculateEmissionsHref(url);
  }, [calculateEmissionsHrefBase]);

  useEffect(() => {
    setCalculateEmissionsHrefBase(getBusinessDefaultURL());
  }, [setCalculateEmissionsHrefBase]);

  return (
    <>
      <Heading as="h1" textAlign="center" mb="10">
        {title}
      </Heading>
      <Tabs isFitted colorScheme="widget" p="0" index={tabIndex}>
        <TabList mb="10">
          <Tab onClick={onIndustryAvgClick}>
            <Heading size="md" as="h3" fontWeight="normal">
              Industry averages
            </Heading>
          </Tab>
          <Tab onClick={onBusinessTabSelect}>
            <Heading size="md" as="h3" fontWeight="normal">
              Business calculator
            </Heading>
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel p="0">
            <EstimationTool
              estimationPresets={estimationPresets}
              onSubmitEstimate={onSubmitEstimate}
              onReset={onBusinessTabSelect}
              countryCode={countryCode}
              calculateEmissionsHref={calculateEmissionsHrefBase}
            />
          </TabPanel>
          <TabPanel p="0">
            <Text pb="5">
              Our business calculator provides you with a more accurate picture
              of your overall business emissions and offers tailored plans for
              offsetting.
            </Text>
            <Text fontWeight="bold" pb="5">
              To use the calculator:
            </Text>
            <UnorderedList mb="10">
              <ListItem mb="3">
                You’ll need to share some information with us such as flights
                taken, distance driven, waste produced and electricity or fuel
                used in the last 12 months.
              </ListItem>
            </UnorderedList>
            <Stack
              direction={{ base: 'column', md: 'row' }}
              alignItems="center"
              spacing="5"
            >
              <Button
                as="a"
                href={calculateEmissionsHref}
                variant="solid"
                target="_top"
              >
                Calculate my emissions
              </Button>
            </Stack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
};
